import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity } from "lucide-react";
import { obApi } from "../api";

/**
 * Activity Pulse — Phase 2 light surface.
 *
 * Plain-language operational events, scoped per the caller's capability map.
 * Phase 4 will deliver the full Activity Pulse module with filtering, deep
 * linking, and the security audit channel split (L6.1).
 *
 * Design contract (design_guidelines.md §4.2):
 *   ●  Sherbrooke updated Review Shield threshold     2h ago
 *   ●  Voucher limits changed for Quebec region       5h ago
 *
 * Severity dot (info / operational / critical) on the left, plain-language
 * sentence in the middle, relative timestamp on the right. Maximum 8
 * events on the home view; never a dense feed.
 */
export const ActivityPulse = ({ limit = 6, "data-testid": testId = "ob-activity-pulse" }) => {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await obApi.activityPulse(limit);
                if (!cancelled) setEvents(res.data?.events || []);
            } catch (err) {
                if (!cancelled) setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [limit]);

    return (
        <section
            data-testid={testId}
            className="ob-card p-6 sm:p-8 space-y-5"
        >
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="ob-tile-icon" aria-hidden="true">
                        <Activity size={18} strokeWidth={1.5} />
                    </div>
                    <div>
                        <p className="ob-eyebrow">{t('operationalBase.whatChanged')}</p>
                        <h2 className="text-xl mt-1">{t('operationalBase.activity')}</h2>
                    </div>
                </div>
            </header>

            {loading && (
                <p
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                    data-testid="ob-activity-loading"
                >
                    Listening…
                </p>
            )}

            {!loading && !error && events.length === 0 && (
                <p
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                    data-testid="ob-activity-empty"
                >
                    Nothing operational has changed yet today.
                </p>
            )}

            {!loading && error && (
                <p
                    className="text-sm"
                    style={{ color: "var(--ob-text-tertiary)" }}
                    data-testid="ob-activity-error"
                >
                    Activity is briefly unavailable.
                </p>
            )}

            {events.length > 0 && (
                <ul className="divide-y divide-white/5" data-testid="ob-activity-list">
                    {events.map((evt, idx) => (
                        <li
                            key={evt.id || `${evt.timestamp}-${idx}`}
                            className="flex items-center gap-4 py-3"
                            data-testid="ob-activity-item"
                        >
                            <span
                                className={`ob-pulse-dot ${evt.severity || "operational"}`}
                                aria-hidden="true"
                            />
                            <p
                                className="flex-1 text-sm leading-relaxed truncate"
                                style={{ color: "var(--ob-text-primary)" }}
                            >
                                {evt.sentence}
                            </p>
                            <span
                                className="text-xs whitespace-nowrap"
                                style={{ color: "var(--ob-text-tertiary)" }}
                            >
                                {evt.relative_time}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};
